<template>
  <BModal
    id="loginModal"
    v-model="uiStore.showLoginModal"
    focus="username"
    :title="success ? 'Successfully Logged in' : 'Login'"
    centered
    @shown="handleOpen"
    @hide="onModalHide"
  >
    <BOverlay :show="success" rounded="sm">
      <template #overlay>
        <div class="text-center">
          <img
            src="~assets/img/icons/checkmark_green.svg"
            width="100%"
            height="100%"
            alt="Success Checkmark"
          />
        </div>
      </template>

      <!-- BACKEND ERRORS -->
      <div class="row mb-3">
        <div
          v-if="backendErr"
          class="col-12 alert-danger"
          style="margin-top: -1rem"
        >
          <div class="row my-3 mb-3 mb-2">
            <div class="col-12">
              <span>{{ backendErr.msg }}</span> -
              <b>{{ backendErr.err }}</b>
            </div>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-12">
          <FormWithValidation
            :key="formKey"
            ref="formRef"
            v-model="loginForm"
            col-class="col-12"
            :initial-state="loginFormInitialState"
            :validation-rules-function="() => getLoginFormRules(loginForm)"
            :validation-messages="validationMessages"
            :field-labels-map="fieldLabelsMap"
            :saved-callback="handleLogin"
            save-button-text="Login"
            :prevent-dirty-navigation="false"
            :save-btn-disabled="friendlyCaptchaEnabled ? !captchaReady : false"
          >
            <template #username="slotProps">
              <FormField
                id="username"
                name="username"
                label="Username"
                mandatory
                :v-field="slotProps.vForm.username"
                :validation-messages="slotProps.validationMessages.username"
                :field-errors="slotProps.fieldErrors"
                :class="slotProps.getIsInvalidClass(slotProps.vForm.username)"
              >
                <InputField
                  id="username"
                  v-model.trim="loginForm.username"
                  :class="slotProps.getIsInvalidClass(slotProps.vForm.username)"
                  name="username"
                  class="form-control form-control-sm"
                  @keyup.enter="handleLogin"
                />
              </FormField>
            </template>
            <template #password="slotProps">
              <FormField
                id="password"
                name="password"
                label="Password"
                mandatory
                :v-field="slotProps.vForm.password"
                :validation-messages="slotProps.validationMessages.password"
                :field-errors="slotProps.fieldErrors"
                :class="slotProps.getIsInvalidClass(slotProps.vForm.password)"
              >
                <InputField
                  id="password"
                  v-model.trim="loginForm.password"
                  :class="slotProps.getIsInvalidClass(slotProps.vForm.password)"
                  name="password"
                  class="form-control form-control-sm"
                  type="password"
                  maxlength="30"
                  autocomplete="off"
                  @keyup.enter="handleLogin"
                />
              </FormField>
            </template>
            <template #authCode="slotProps">
              <FormField
                v-if="showAuthCode"
                id="authCode"
                name="authCode"
                label="2FA Code"
                :v-field="slotProps.vForm.authCode"
                :validation-messages="slotProps.validationMessages.authCode"
                :field-errors="slotProps.fieldErrors"
                help-text="Required if Two-Factor Authentication (2FA) is activated"
                :class="slotProps.getIsInvalidClass(slotProps.vForm.authCode)"
              >
                <InputField
                  id="authCode"
                  v-model.trim="loginForm.authCode"
                  :class="slotProps.getIsInvalidClass(slotProps.vForm.authCode)"
                  name="authCode"
                  class="form-control form-control-sm"
                  @keyup.enter="handleLogin"
                />
              </FormField>
            </template>

            <template #requestMfaDeviceToken="slotProps">
              <FormField
                v-if="showAuthCode"
                id="requestMfaDeviceToken"
                name="requestMfaDeviceToken"
                label="Remember this device"
                :v-field="slotProps.vForm.requestMfaDeviceToken"
                :validation-messages="
                  slotProps.validationMessages.requestMfaDeviceToken
                "
                :field-errors="slotProps.fieldErrors"
                :class="
                  slotProps.getIsInvalidClass(
                    slotProps.vForm.requestMfaDeviceToken,
                  )
                "
              >
                <div
                  class="mt-2 d-flex justify-content-between align-items-center"
                  :class="
                    slotProps.getIsInvalidClass(
                      slotProps.vForm.requestMfaDeviceToken,
                    )
                  "
                >
                  <CheckBoxField
                    id="requestMfaDeviceToken"
                    v-model="loginForm.requestMfaDeviceToken"
                    :class="
                      slotProps.getIsInvalidClass(
                        slotProps.vForm.requestMfaDeviceToken,
                      )
                    "
                    name="requestMfaDeviceToken"
                  />
                  <HelpBadgeCircle :tip-html="twoFaHelpTip" />
                </div>
              </FormField>
            </template>

            <template #captcha>
              <FormField
                v-if="friendlyCaptchaEnabled"
                id="captcha"
                name="captcha"
                label="Captcha"
              >
                <CaptchaComponent
                  ref="friendlyCaptchaRef"
                  start="auto"
                  @complete="captchaDone"
                  @error="captchaError"
                ></CaptchaComponent>
              </FormField>
            </template>

            <template #cancel-button>
              <StoreButton @click="handleCancel"> Cancel</StoreButton>
            </template>
          </FormWithValidation>
        </div>
      </div>
      <div class="row">
        <div class="col-12">
          <hr />
        </div>
      </div>

      <!-- Reset password -->
      <div class="row mb-3">
        <div class="col-12">
          <StoreLink to="/reset-password" @click="handleCancel"
            >Having problems with your login?
          </StoreLink>
        </div>
      </div>
      <div class="row">
        <div class="col-12">
          <p>
            Information on the processing of your personal data can be found in
            our
            <StoreLink target="_blank" :href="privacyPolicyLink"
              >Privacy Policy
            </StoreLink>
          </p>
        </div>
      </div>
    </BOverlay>
    <template #footer>
      <div class="w-100 text-center">
        <h6>Create a business customer account</h6>
        <StoreButton class="mx-2" @click="handleRegister">
          Register now
        </StoreButton>
      </div>
    </template>
  </BModal>
</template>

<script lang="ts" setup>
import { ref } from "vue";
import { useRouter, useRoute } from "#imports";
import { BOverlay, BModal } from "bootstrap-vue-next";
import StoreLink from "~/components/shared/buttons/StoreLink.vue";
import { useUiStore } from "~/stores/ui/uistore";
import { useUserStore } from "~/stores/userstore";
import type { StoreApiError } from "~/model/api/pLCnextStoreFrontendAPI.schemas";
import { privacyPolicyLink } from "~~/public/constants";
import StoreButton from "~/components/shared/buttons/StoreButton.vue";
import CaptchaComponent from "~/components/shared/CaptchaComponent.vue";
import { useDeployementInfoStore } from "~/stores/deploymentinfostore";
import FormWithValidation from "~/components/shared/forms/FormWithValidation.vue";
import FormField from "~/components/shared/forms/FormField.vue";
import {
  getLoginFormRules,
  type LoginForm,
  validationMessages,
  fieldLabelsMap,
} from "~/validations/login";
import InputField from "~/components/shared/inputfields/InputField.vue";
import CheckBoxField from "~/components/shared/inputfields/CheckBoxField.vue";
import HelpBadgeCircle from "~/components/shared/buttons/HelpBadgeCircle.vue";
import {
  isStoreApiError,
  isAxiosError,
  getErrorAndMessage,
} from "~/plugins/errorMessage.client";

/* ------------------------------------------------------------------ */
/* STORE & STATE */
/* ------------------------------------------------------------------ */
const uiStore = useUiStore();
const userStore = useUserStore();
const router = useRouter();
const route = useRoute();

const formKey = ref(0);
const formRef = ref<{ resetForm: () => void } | null>(null);

const loginFormInitialState = reactive({
  username: "",
  password: "",
  authCode: "",
  requestMfaDeviceToken: false,
});

const loginForm = reactive<LoginForm>({
  ...loginFormInitialState,
});

const backendErr = ref<{ msg: string; err: string } | null>(null);
const success = ref(false);

const captchaReady = ref(false);
const captchaString = ref("");
const friendlyCaptchaRef = ref(null);
const deployementInfoStore = useDeployementInfoStore();

const friendlyCaptchaEnabled = computed(
  () => deployementInfoStore.deploymentInfo?.friendlyCaptchaEnabled,
);
const mfaDeviceTokenCookieValidityInDays = computed(
  () => deployementInfoStore.deploymentInfo?.mfaDeviceTokenCookieValidityInDays,
);

const showAuthCode = computed(() => {
  if (
    userStore.mfaDeviceTokenCookieInfo &&
    userStore.mfaDeviceTokenCookieInfo.username
  ) {
    return (
      loginForm.username.toLowerCase() !==
      userStore.mfaDeviceTokenCookieInfo.username.toLowerCase()
    );
  } else {
    return true;
  }
});

/* ------------------------------------------------------------------ */
/* CAPTCHA */
/* ------------------------------------------------------------------ */
function captchaError(error: unknown) {
  console.error("captchaError", error);
  backendErr.value = { msg: "Captcha was not solved", err: error as string };
}

function captchaDone(captcha: string) {
  captchaString.value = captcha;
  captchaReady.value = true;
}

/* ------------------------------------------------------------------ */
/* UI ACTIONS */
/* ------------------------------------------------------------------ */

async function handleOpen() {
  await userStore.loadMfaDeviceTokenInfo();
  resetForm();
}

const onModalHide = () => {
  formRef.value?.resetForm();
  useUiStore().isFormDirty = false;
};

function resetForm() {
  loginForm.username = "";
  loginFormInitialState.username = "";
  formKey.value += 1; // vue rerender so that everything is reset
  success.value = false;

  if (
    userStore.mfaDeviceTokenCookieInfo &&
    userStore.mfaDeviceTokenCookieInfo.username
  ) {
    loginForm.username = userStore.mfaDeviceTokenCookieInfo.username;
  }

  // Reset backend error message
  backendErr.value = null;

  // Reset captcha if enabled
  if (friendlyCaptchaRef.value) {
    captchaReady.value = false;
    //@ts-expect-error third party lib friendlyCaptchaRef.value.reset() is not typed correctly
    friendlyCaptchaRef.value.reset();
  }
}

function handleCancel() {
  uiStore.showLoginModal = false;
  if (route.path === "/register/confirm") {
    router.push("/");
  }
}

function handleRegister() {
  router.push("/register");
  handleCancel();
}

/* ------------------------------------------------------------------ */
/* LOGIN */
/* ------------------------------------------------------------------ */
async function handleLogin() {
  if (friendlyCaptchaEnabled.value && !captchaReady.value) return;

  const { success: ok, err: loginErr } = await userStore.logUserIn({
    username: loginForm.username,
    password: loginForm.password,
    developerRequest: uiStore.becomeDeveloperAfterLogin,
    captcha: captchaString.value,
    authCode: loginForm.authCode,
    requestMfaDeviceToken: loginForm.requestMfaDeviceToken,
  });

  if (ok) {
    success.value = true;
    setTimeout(() => {
      uiStore.showLoginModal = false;
      uiStore.isFormDirty = false;
      if (uiStore.loginRedirectLink) {
        location.assign(uiStore.loginRedirectLink);
      } else {
        location.reload();
      }
    }, 1500);
    return;
  }

  backendErr.value = setBackendError(loginErr);

  if (friendlyCaptchaRef.value) {
    captchaReady.value = false;
    //@ts-expect-error third party lib friendlyCaptchaRef.value.reset() is not typed correctly
    friendlyCaptchaRef.value.reset();
  }
}

const setBackendError = (loginErr: unknown) => {
  if (isAxiosError(loginErr)) {
    if (isStoreApiError(loginErr.response?.data)) {
      const apiError = loginErr.response.data as StoreApiError;
      if (
        //@ts-expect-error StoreApiErrorStatus is not typed correctly
        apiError.status == "NOT_FOUND" &&
        apiError.errors?.includes("User not found")
      ) {
        // provide the user a better better message in case of a user not found error
        return {
          msg: "Login failed",
          err: "The provided login details are incorrect.",
        };
      }
    }
  }
  const errorAndMessage = getErrorAndMessage(loginErr);
  return {
    err: errorAndMessage.error,
    msg: errorAndMessage.message,
  };
};

const twoFaHelpTip =
  "2FA codes will not be requested on this device for the next " +
  mfaDeviceTokenCookieValidityInDays.value +
  " days";
</script>
<style lang="scss" scoped>
@use "@/sass/components/LoginModal.scss" as *;
</style>
